import { t } from 'i18next';
import { FC } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  InfoSpotDetailsFragment,
  StopRegistryIntendedUser,
} from '../../../../../../generated/graphql';
import {
  mapIntendedUserToUiName,
  mapZoneLabelToUiName,
} from '../../../../../../utils/i18n';
import { AddNewButton, SimpleButton } from '../../../../../common/Buttons';
import {
  EnumDropdown,
  NullableBooleanDropdown,
} from '../../../../../common/Dropdowns';
import { InputField } from '../../../../../common/Inputs';
import { Column, Row } from '../../../../../common/LayoutComponents';
import {
  InfoSpotsFormState,
  PositionMoveControls,
  PosterState,
  SizeFormFragment,
  defaultInfoSpotPosterValues,
  mapStringToPurpose,
} from '../../../../Components/InfoSpots';
import { ZoneLabel } from '../../../../Types';
import { InfoSpotsFormPosters } from './InfoSpotsFormPosters';

const testIds = {
  description: 'InfoSpotFormFields::description',
  label: 'InfoSpotFormFields::label',
  intendedUser: 'InfoSpotFormFields::intendedUser',
  latitude: 'InfoSpotFormFields::latitude',
  longitude: 'InfoSpotFormFields::longitude',
  backlight: 'InfoSpotFormFields::backlight',
  floor: 'InfoSpotFormFields::floor',
  railInformation: 'InfoSpotFormFields::railInformation',
  stops: 'InfoSpotFormFields::stops',
  terminals: 'InfoSpotFormFields::terminals',
  zoneLabel: 'InfoSpotFormFields::zoneLabel',
  posterSize: 'InfoSpotPosterFormFields::posterSize',
  posterLines: 'InfoSpotPosterFormFields::posterLines',
  deleteInfoSpot: 'InfoSpotFormFields::deleteInfoSpot',
  addInfoSpotPoster: 'InfoSpotFormFields::addInfoSpotPoster',
  noPosters: 'InfoSpotFormFields::noPosters',
};

type InfoSpotFormFieldsProps = {
  readonly infoSpotIndex: number;
  readonly infoSpotsData: ReadonlyArray<InfoSpotDetailsFragment>;
  readonly onRemove: (index: number) => void;
  readonly onMoveUp: (index: number) => void;
  readonly onMoveDown: (index: number) => void;
  readonly isFirst: boolean;
  readonly isLast: boolean;
  readonly totalCount: number;
};

export const InfoSpotFormFields: FC<InfoSpotFormFieldsProps> = ({
  infoSpotIndex,
  infoSpotsData,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  totalCount,
}) => {
  const { register, watch, getValues, setValue, control } =
    useFormContext<InfoSpotsFormState>();
  const toBeDeleted = watch(`infoSpots.${infoSpotIndex}.toBeDeleted`);

  const {
    fields: posters,
    append: appendPoster,
    move: movePoster,
  } = useFieldArray({
    control,
    name: `infoSpots.${infoSpotIndex}.poster`,
  });

  const addPoster = () => {
    const newPoster: PosterState = {
      size: {
        uiState: 'EXISTING',
        width: defaultInfoSpotPosterValues.width,
        height: defaultInfoSpotPosterValues.height,
      },
      label: mapStringToPurpose(defaultInfoSpotPosterValues.label),
      lines: '',
      toBeDeletedPoster: false,
      sortOrder: null,
      id: null,
    };
    appendPoster(newPoster);
  };

  const onRemovePoster = (idx: number, posterIndex: number) => {
    const newToBeDeleted = !getValues(
      `infoSpots.${idx}.poster.${posterIndex}.toBeDeletedPoster`,
    );
    setValue(
      `infoSpots.${idx}.poster.${posterIndex}.toBeDeletedPoster`,
      newToBeDeleted,
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true, // Add this to trigger revalidation
      },
    );
  };

  const onMovePosterUp = (posterIndex: number) => {
    if (posterIndex > 0) {
      movePoster(posterIndex, posterIndex - 1);
    }
  };

  const onMovePosterDown = (posterIndex: number) => {
    if (posterIndex < posters.length - 1) {
      movePoster(posterIndex, posterIndex + 1);
    }
  };

  return (
    <Column>
      <div className="bg-background p-5">
        <span className="mb-2.5 block text-xl">
          <i
            className="icon-passenger-info mr-2.5 text-brand"
            role="presentation"
          />
          {t(($) => $.stopDetails.infoSpots.infoSpot, {
            infoSpot: infoSpotsData[infoSpotIndex]?.label,
          })}
        </span>
        <Row className="flex-wrap items-end gap-4 py-2.5">
          <InputField<InfoSpotsFormState>
            type="text"
            translationPrefix="stopDetails"
            fieldPath={`infoSpots.${infoSpotIndex}.label`}
            testId={testIds.label}
            disabled={toBeDeleted}
          />

          <InputField<InfoSpotsFormState>
            translationPrefix="stopDetails"
            fieldPath={`infoSpots.${infoSpotIndex}.intendedUser`}
            testId={testIds.intendedUser}

            inputElementRenderer={(props) => (
              <EnumDropdown<StopRegistryIntendedUser>
                enumType={StopRegistryIntendedUser}
                placeholder={t(($) => $.unknown)}
                uiNameMapper={(val) => mapIntendedUserToUiName(t, val)}
                buttonClassName="min-w-32"
                includeNullOption
                disabled={toBeDeleted}
                customOrder={[
                  StopRegistryIntendedUser.Matkatieto,
                  StopRegistryIntendedUser.Markkinointi,
                  StopRegistryIntendedUser.Vr,
                  StopRegistryIntendedUser.Muu,
                ]}
                {...props}
              />
            )}
          />

          <SizeFormFragment<InfoSpotsFormState>
            sizeStatePath={`infoSpots.${infoSpotIndex}.size`}
            titlePath="stopDetails.infoSpots.size"
            disabled={toBeDeleted}
          />

          <InputField<InfoSpotsFormState, boolean | null>
            translationPrefix="stopDetails"
            fieldPath={`infoSpots.${infoSpotIndex}.backlight`}
            testId={testIds.backlight}

            inputElementRenderer={(props) => (
              <NullableBooleanDropdown
                placeholder={t(($) => $.unknown)}
                buttonClassName="min-w-32"
                disabled={toBeDeleted}
                {...props}
              />
            )}
          />
        </Row>
        <Row className="flex-wrap items-end gap-4 py-2.5 lg:flex-nowrap">
          <InputField<InfoSpotsFormState>
            translationPrefix="stopDetails"
            fieldPath={`infoSpots.${infoSpotIndex}.zoneLabel`}
            testId={testIds.zoneLabel}

            inputElementRenderer={(props) => (
              <EnumDropdown<ZoneLabel>
                enumType={ZoneLabel}
                placeholder={t(($) => $.unknown)}
                uiNameMapper={(val) => mapZoneLabelToUiName(t, val)}
                buttonClassName="min-w-32"
                disabled={toBeDeleted}
                {...props}
              />
            )}
          />

          <InputField<InfoSpotsFormState>
            type="text"
            translationPrefix="stopDetails"
            fieldPath={`infoSpots.${infoSpotIndex}.railInformation`}
            inputClassName="w-20"
            testId={testIds.railInformation}
            disabled={toBeDeleted}
          />

          <InputField<InfoSpotsFormState>
            type="text"
            translationPrefix="stopDetails"
            fieldPath={`infoSpots.${infoSpotIndex}.floor`}
            inputClassName="w-20"
            testId={testIds.floor}
            disabled={toBeDeleted}
          />
        </Row>
        <Column className="items-stretch gap-4 bg-background py-2.5">
          <InputField<InfoSpotsFormState>
            type="text"
            translationPrefix="stopDetails"
            fieldPath={`infoSpots.${infoSpotIndex}.description.value`}
            testId={testIds.description}
            customTitlePath="stopDetails.infoSpots.description"
            disabled={toBeDeleted}
          />

          <Row className="justify-between gap-2">
            <SimpleButton
              shape="slim"
              testId={testIds.deleteInfoSpot}
              onClick={() => onRemove(infoSpotIndex)}
              inverted
            >
              {toBeDeleted
                ? t(($) => $.stopDetails.infoSpots.cancelDeleteInfoSpot)
                : t(($) => $.stopDetails.infoSpots.deleteInfoSpot)}
            </SimpleButton>
            <PositionMoveControls
              position={infoSpotIndex + 1}
              total={totalCount}
              isFirst={isFirst}
              isLast={isLast}
              disabled={toBeDeleted}
              onMoveUp={() => onMoveUp(infoSpotIndex)}
              onMoveDown={() => onMoveDown(infoSpotIndex)}
              testIdPrefix="InfoSpotFormFields"
            />
          </Row>
        </Column>
      </div>
      {posters?.length ? (
        posters.map((poster, posterIndex) => (
          <InfoSpotsFormPosters
            key={poster.id}
            infoSpotIndex={infoSpotIndex}
            posterIndex={posterIndex}
            addPoster={addPoster}
            onRemovePoster={onRemovePoster}
            onMovePosterUp={onMovePosterUp}
            onMovePosterDown={onMovePosterDown}
            isFirstPoster={posterIndex === 0}
            isLastPoster={posterIndex === posters.length - 1}
            totalPosters={posters.length}
            infoSpotToBeDeleted={toBeDeleted}
          />
        ))
      ) : (
        <Row className="items-center p-5">
          <span data-testid={testIds.noPosters}>
            <i className="icon-alert mr-2.5 text-hsl-red" role="presentation" />
            {t(($) => $.stopDetails.infoSpots.noPosters)}
          </span>
          <AddNewButton
            testId={testIds.addInfoSpotPoster}
            label={t(($) => $.stopDetails.infoSpots.addInfoSpotPoster)}
            onClick={addPoster}
            className="ml-auto"
          />
        </Row>
      )}
      <input
        type="checkbox"
        hidden
        {...register(`infoSpots.${infoSpotIndex}.toBeDeleted`)}
      />
    </Column>
  );
};
